/**
 * Load reporting user context from profile and preferences
 */

import { supabase } from '../../lib/supabase';
import type { ReportingUserContext } from '../../types/reporting-user-context';

const COUNTRY_NAME_TO_CODE: Record<string, string> = {
  Australia: 'AU',
  'New Zealand': 'NZ',
  'United Kingdom': 'GB',
  'United States': 'US',
  Canada: 'CA',
};

const FY_START_TO_MONTH: Record<string, number> = {
  January: 1,
  April: 4,
  July: 7,
  October: 10,
};

export const AU_ONLY_REPORT_TYPES = ['tax_deduction', 'year_end_tax_summary'] as const;

export function countryNameToCode(countryName: string): string {
  return COUNTRY_NAME_TO_CODE[countryName] ?? 'AU';
}

export function financialYearStartToMonth(fyStart: string | null | undefined): number {
  if (fyStart && fyStart in FY_START_TO_MONTH) {
    return FY_START_TO_MONTH[fyStart];
  }
  return 7;
}

export function getReportingDisclaimer(countryCode: string): string {
  if (countryCode === 'AU') {
    return (
      'This report is for informational purposes only and is not tax or financial advice. ' +
      'Consult a registered tax agent or the ATO for obligations specific to your situation.'
    );
  }
  return (
    'This report is for informational purposes only and is not tax or financial advice. ' +
    'Consult a qualified professional for obligations in your jurisdiction.'
  );
}

export const DEFAULT_REPORTING_USER_CONTEXT: ReportingUserContext = {
  countryCode: 'AU',
  countryName: 'Australia',
  financialYearStartMonth: 7,
  currency: 'AUD',
  timezone: null,
  gstRegistered: false,
  businessName: null,
  businessAbn: null,
};

const PREFS_SELECT_CORE = 'financial_year_start, currency, timezone';
const PREFS_SELECT_TAX = 'gst_registered, business_name, business_abn';

async function loadUserPreferencesRow(userId: string) {
  const extended = await supabase
    .from('user_preferences')
    .select(`${PREFS_SELECT_CORE}, ${PREFS_SELECT_TAX}`)
    .eq('user_id', userId)
    .maybeSingle();

  if (
    extended.error &&
    /column .+ does not exist/i.test(extended.error.message)
  ) {
    return supabase
      .from('user_preferences')
      .select(PREFS_SELECT_CORE)
      .eq('user_id', userId)
      .maybeSingle();
  }

  return extended;
}

export async function loadReportingUserContext(userId: string): Promise<ReportingUserContext> {
  const [profileRes, prefsRes] = await Promise.all([
    supabase.from('user_profiles').select('country_of_residence').eq('user_id', userId).maybeSingle(),
    loadUserPreferencesRow(userId),
  ]);

  const countryName = (profileRes.data?.country_of_residence as string | null) ?? 'Australia';
  const countryCode = countryNameToCode(countryName);
  const fyStart = prefsRes.data?.financial_year_start as string | null | undefined;

  const prefs = prefsRes.data as {
    currency?: string | null;
    timezone?: string | null;
    gst_registered?: boolean | null;
    business_name?: string | null;
    business_abn?: string | null;
  } | null;

  return {
    countryCode,
    countryName,
    financialYearStartMonth: financialYearStartToMonth(fyStart),
    currency: prefs?.currency ?? 'AUD',
    timezone: prefs?.timezone ?? null,
    gstRegistered: Boolean(prefs?.gst_registered),
    businessName: prefs?.business_name ?? null,
    businessAbn: prefs?.business_abn ?? null,
  };
}

export function isAuOnlyReportAvailable(countryCode: string): boolean {
  return countryCode === 'AU';
}
